import React from "react";

import { Type, isNodeOwned } from "~/models/dto";
import { Permitted } from "~/tools/permitted";

import { RightPaneState } from "../../app/state/right-pane/RightPaneState";
import {
	useCanDeleteCurrentView,
	useCanEditCurrentView,
	useCanResetCurrentQuery,
	useCanSaveCurrentView,
	useCanSaveCurrentViewAs,
} from "../../app/state/view/viewpoint/CanDoHooks";
import {
	useCurrentInitialView,
	useCurrentView,
	useEditView,
	useSaveViewAs,
} from "../../app/state/view/viewpoint/ViewPointHooks";
import { Messages } from "../../common/Messages";
import { useNavigateFrom } from "../../components/react/link";
import { SaveButton } from "../../components/react/save/SaveButton";
import { usePersistNodeFlow } from "../../queries";

export const SaveViewButton: React.FC = () => {
	const enableReset = useCanResetCurrentQuery();
	const enableSaveAs = useCanSaveCurrentViewAs();
	const enableSave = useCanSaveCurrentView();
	const currentViewWithChanges = useCurrentView();
	const saveViewAs = useSaveViewAs();
	const enableEdit = useCanEditCurrentView();
	const enableDelete = useCanDeleteCurrentView();
	const currentView = useCurrentInitialView();
	const editView = useEditView();
	const navigateFrom = useNavigateFrom();
	const { mutate: mutateView } = usePersistNodeFlow(Type.QueryView, "view");

	return Permitted.all({
		view: currentView,
		viewWithChanges: currentViewWithChanges,
	}).match({
		denied: () => null,

		granted: ({ value: { view, viewWithChanges } }) => (
			<SaveButton
				reset={{
					enabler: enableReset,
					title: "Reset view",
					handler: () => {
						navigateFrom("*", {
							search: (search) => ({
								...search,
								rightPane: RightPaneState.resetWhenVisible(search.rightPane),
								view: undefined,
								query: undefined,
								// reset detached filters for locked views
								detachedFilters: isNodeOwned(view) ? undefined : search.detachedFilters,
							}),
						});
					},
				}}
				rest={[
					{
						enabler: enableEdit,
						title: "Edit view properties",
						handler: () => editView(view),
					},
					{
						enabler: enableDelete,
						title: "Delete view",
						handler: () => void mutateView("delete", view, Messages.deleteConfirm),
					},
				]}
				save={{
					isPrimary: true,
					enabler: enableSave,
					title: "Save view",
					handler: () => void mutateView("save", viewWithChanges),
				}}
				saveAs={{
					enabler: enableSaveAs,
					title: "Save view as...",
					handler: () => saveViewAs(viewWithChanges),
				}}
			/>
		),
	});
};
